import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { ArrowLeft, DollarSign, Calendar, CheckCircle, Clock, Settings, CalendarPlus, Search, Camera, BarChart3 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { token, logout } = useAuth();
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState({ totalBookings: 0, pendingBookings: 0, completedInspections: 0, totalRevenue: 0 });
  const [showSettings, setShowSettings] = useState(false);
  const [reviewBooking, setReviewBooking] = useState(null);
  const [checks, setChecks] = useState({ seller: false, location: false, time: false });
  const [approving, setApproving] = useState(false);
  const [feeInput, setFeeInput] = useState('0');
  const [settings, setSettings] = useState({ samedayCutoffHour: 11, samedayRadiusMiles: 20, samedayLastStartHour: 17, blockedDates: [], dateSurcharges: [] });
  const [savingSettings, setSavingSettings] = useState(false);
  const [newBlockDate, setNewBlockDate] = useState('');
  const [newBlockMsg, setNewBlockMsg] = useState('');
  const [newFeeDate, setNewFeeDate] = useState('');
  const [newFeeAmount, setNewFeeAmount] = useState('');
  const [newFeeLabel, setNewFeeLabel] = useState('Holiday Service Fee');

  useEffect(() => {
    fetchData();
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const res = await axios.get(`${API}/settings`);
      setSettings({
        samedayCutoffHour: res.data.samedayCutoffHour ?? 11,
        samedayRadiusMiles: res.data.samedayRadiusMiles ?? 20,
        samedayLastStartHour: res.data.samedayLastStartHour ?? 17,
        blockedDates: res.data.blockedDates || [],
        dateSurcharges: res.data.dateSurcharges || [],
      });
    } catch (e) { console.error('Failed to load settings', e); }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await axios.patch(`${API}/settings`, {
        samedayCutoffHour: Number(settings.samedayCutoffHour),
        samedayRadiusMiles: Number(settings.samedayRadiusMiles),
        samedayLastStartHour: Number(settings.samedayLastStartHour),
        blockedDates: settings.blockedDates,
        dateSurcharges: settings.dateSurcharges,
      }, authHeaders);
      toast.success('Scheduling settings saved');
      loadSettings();
    } catch (e) {
      console.error('Failed to save settings', e);
      toast.error('Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const addBlockedDate = () => {
    if (!newBlockDate) { toast.error('Pick a date to block'); return; }
    if (settings.blockedDates.some((b) => b.date === newBlockDate)) { toast.error('That date is already blocked'); return; }
    setSettings({ ...settings, blockedDates: [...settings.blockedDates, { date: newBlockDate, message: newBlockMsg.trim() || null }] });
    setNewBlockDate(''); setNewBlockMsg('');
  };

  const removeBlockedDate = (date) => {
    setSettings({ ...settings, blockedDates: settings.blockedDates.filter((b) => b.date !== date) });
  };

  const addDateFee = () => {
    if (!newFeeDate) { toast.error('Pick a date for the fee'); return; }
    const amt = Number(newFeeAmount);
    if (!amt || amt <= 0) { toast.error('Enter a valid fee amount'); return; }
    if (settings.dateSurcharges.some((d) => d.date === newFeeDate)) { toast.error('That date already has a fee'); return; }
    setSettings({ ...settings, dateSurcharges: [...settings.dateSurcharges, { date: newFeeDate, amount: amt, label: newFeeLabel.trim() || 'Holiday Service Fee' }] });
    setNewFeeDate(''); setNewFeeAmount(''); setNewFeeLabel('Holiday Service Fee');
  };

  const removeDateFee = (date) => {
    setSettings({ ...settings, dateSurcharges: settings.dateSurcharges.filter((d) => d.date !== date) });
  };


  const fetchData = async () => {
    try {
      const [bookingsRes, statsRes] = await Promise.all([
        axios.get(`${API}/bookings`, authHeaders),
        axios.get(`${API}/stats`, authHeaders)
      ]);
      
      setBookings(bookingsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load dashboard data');
    }
  };

  const openReview = (booking) => {
    setReviewBooking(booking);
    setChecks({ seller: false, location: false, time: false });
    setFeeInput(String(booking.travelFee != null ? booking.travelFee : 0));
  };

  const approveBooking = async () => {
    if (!reviewBooking) return;
    setApproving(true);
    try {
      // If the owner adjusted the travel fee, persist it first (server recomputes the total).
      const fee = Math.max(0, parseFloat(feeInput) || 0);
      if (Math.abs(fee - (reviewBooking.travelFee || 0)) > 0.001) {
        await axios.patch(`${API}/bookings/${reviewBooking.id}`, { travelFee: fee }, authHeaders);
      }
      await axios.post(`${API}/bookings/${reviewBooking.id}/approve`, {
        origin_url: window.location.origin
      }, authHeaders);
      toast.success('Approved! Secure payment link emailed to the customer.');
      setReviewBooking(null);
      fetchData();
    } catch (error) {
      console.error('Error approving booking:', error);
      toast.error('Failed to approve and send payment link');
    } finally {
      setApproving(false);
    }
  };

  const declineBooking = async (bookingId) => {
    try {
      await axios.post(`${API}/bookings/${bookingId}/decline`, {}, authHeaders);
      toast.success('Booking request declined');
      setReviewBooking(null);
      fetchData();
    } catch (error) {
      console.error('Error declining booking:', error);
      toast.error('Failed to decline booking');
    }
  };

  const markConfirmed = async (bookingId) => {
    if (!window.confirm('Mark this booking as Confirmed? Use this only if payment was handled off-platform (e.g. cash). The customer will get a confirmation email.')) return;
    try {
      await axios.post(`${API}/bookings/${bookingId}/mark-confirmed`, { markPaid: true }, authHeaders);
      toast.success('Booking marked as Confirmed');
      fetchData();
    } catch (error) {
      console.error('Error marking confirmed:', error);
      toast.error('Failed to mark booking as confirmed');
    }
  };

  const statusLabels = {
    pending_review: 'Pending Review',
    approved_awaiting_payment: 'Awaiting Payment',
    confirmed: 'Confirmed',
    in_progress: 'In Progress',
    completed: 'Completed',
    declined: 'Declined',
    cancelled: 'Cancelled',
    expired: 'Link Expired',
    pending: 'Pending Review',
  };

  const getStatusColor = (status) => {
    const colors = {
      pending_review: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/30',
      approved_awaiting_payment: 'bg-orange-500/10 text-orange-500 border-orange-500/30',
      confirmed: 'bg-zinc-600/10 text-zinc-600 border-zinc-600/30',
      in_progress: 'bg-purple-500/10 text-purple-500 border-purple-500/30',
      completed: 'bg-green-500/10 text-green-500 border-green-500/30',
      declined: 'bg-red-500/10 text-red-500 border-red-500/30',
      cancelled: 'bg-red-500/10 text-red-500 border-red-500/30',
      expired: 'bg-zinc-500/10 text-zinc-400 border-zinc-500/30',
      pending: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/30',
    };
    return colors[status] || colors.pending_review;
  };

  const allChecked = checks.seller && checks.location && checks.time;

  const addToCalendar = (booking) => {
    // Build start/end datetime from preferredDate + preferredTime
    const dateStr = (booking.preferredDate || '').replace(/-/g, '');
    let hour = 10, minute = 0;
    const t = (booking.preferredTime || '').trim();
    const isPM = /pm/i.test(t);
    const isAM = /am/i.test(t);
    const clean = t.replace(/am|pm/i, '').trim();
    const parts = clean.split(':');
    if (parts[0]) hour = parseInt(parts[0], 10) || 0;
    if (parts[1]) minute = parseInt(parts[1], 10) || 0;
    if (isPM && hour !== 12) hour += 12;
    if (isAM && hour === 12) hour = 0;
    const pad = (n) => String(n).padStart(2, '0');
    const start = `${dateStr}T${pad(hour)}${pad(minute)}00`;
    const endHour = (hour + 2) % 24;
    const end = `${dateStr}T${pad(endHour)}${pad(minute)}00`;

    const title = `CarLens Inspection - ${booking.carYear} ${booking.carMake} ${booking.carModel}`;
    const details = `Customer: ${booking.customerName}\nPhone: ${booking.phone}\nEmail: ${booking.email}\nVIN: ${booking.vin}\nTotal: $${(booking.totalAmount || 0).toFixed(2)}`;
    const url = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(title)}&dates=${start}/${end}&details=${encodeURIComponent(details)}&location=${encodeURIComponent(booking.location || '')}`;
    window.open(url, '_blank');
  };

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #141416 0%, #1f1f23 50%, #161618 100%)' }}>
      <header className="border-b border-zinc-800 backdrop-blur-sm bg-black/30">
        <div className="max-w-7xl mx-auto pl-6 pr-20 sm:pr-32 py-4 flex justify-between items-center">
          <Button 
            variant="ghost" 
            className="text-zinc-300 hover:text-white hover:bg-white/10"
            onClick={() => navigate('/')}
            data-testid="back-home-admin-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Button>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              className="border-zinc-700 text-zinc-300 hover:bg-white/5"
              onClick={() => setShowSettings(!showSettings)}
              data-testid="toggle-settings-btn"
            >
              <Settings className="w-4 h-4 mr-2" />
              Settings
            </Button>
            <Button
              variant="outline"
              className="border-orange-500/40 text-orange-300 hover:bg-orange-500/10"
              onClick={() => navigate('/admin/firstlook')}
              data-testid="admin-firstlook-btn"
            >
              <Search className="w-4 h-4 mr-2" />
              FirstLook Leads
            </Button>
            <Button
              variant="outline"
              className="border-orange-500/40 text-orange-300 hover:bg-orange-500/10"
              onClick={() => navigate('/admin/inspections')}
              data-testid="admin-inspections-btn"
            >
              <Camera className="w-4 h-4 mr-2" />
              Recent Inspections
            </Button>
            <Button
              variant="outline"
              className="border-zinc-700 text-zinc-300 hover:bg-white/5"
              onClick={() => { logout(); navigate('/'); }}
              data-testid="admin-logout-btn"
            >
              Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-12">
        <h1 className="text-4xl font-bold text-white mb-8" style={{ fontFamily: 'Space Grotesk' }}>
          Admin Dashboard
        </h1>

        {/* Settings Panel */}
        {showSettings && (
          <div className="bg-gradient-to-br from-zinc-900 to-zinc-950 rounded-2xl border border-zinc-800 p-8 mb-8" data-testid="settings-panel">
            <h2 className="text-2xl font-semibold text-white mb-6">Scheduling Controls</h2>

            {/* Same-Day rules */}
            <div className="grid sm:grid-cols-3 gap-5 mb-8">
              <div>
                <label className="block text-sm text-zinc-300 mb-2">Same-Day cutoff hour (24h ET)</label>
                <input type="number" min="0" max="23" value={settings.samedayCutoffHour}
                  onChange={(e) => setSettings({ ...settings, samedayCutoffHour: e.target.value })}
                  className="w-full bg-zinc-950 border border-zinc-700 rounded-md px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                  data-testid="setting-cutoff-input" />
                <p className="text-xs text-zinc-500 mt-1">e.g. 11 = requests must be placed before 11:00 AM ET</p>
              </div>
              <div>
                <label className="block text-sm text-zinc-300 mb-2">Same-Day service radius (miles)</label>
                <input type="number" min="1" step="1" value={settings.samedayRadiusMiles}
                  onChange={(e) => setSettings({ ...settings, samedayRadiusMiles: e.target.value })}
                  className="w-full bg-zinc-950 border border-zinc-700 rounded-md px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                  data-testid="setting-radius-input" />
              </div>
              <div>
                <label className="block text-sm text-zinc-300 mb-2">Latest same-day start hour (24h)</label>
                <input type="number" min="0" max="23" value={settings.samedayLastStartHour}
                  onChange={(e) => setSettings({ ...settings, samedayLastStartHour: e.target.value })}
                  className="w-full bg-zinc-950 border border-zinc-700 rounded-md px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                  data-testid="setting-laststart-input" />
                <p className="text-xs text-zinc-500 mt-1">e.g. 17 = must start by 5:00 PM</p>
              </div>
            </div>

            {/* Blocked dates */}
            <h3 className="text-lg font-semibold text-white mb-3">Blocked Dates</h3>
            <p className="text-sm text-zinc-400 mb-4">Customers can't book these dates. Add an optional message shown to them (e.g. next available day + your phone number).</p>
            <div className="space-y-2 mb-5" data-testid="blocked-dates-list">
              {settings.blockedDates.length === 0 && <p className="text-sm text-zinc-500">No blocked dates.</p>}
              {settings.blockedDates.map((b) => (
                <div key={b.date} className="flex items-start justify-between gap-3 bg-zinc-950 border border-zinc-800 rounded-lg px-4 py-3" data-testid={`blocked-date-${b.date}`}>
                  <div className="min-w-0">
                    <p className="text-white font-medium">{b.date}</p>
                    {b.message && <p className="text-xs text-zinc-400 mt-0.5">{b.message}</p>}
                  </div>
                  <button onClick={() => removeBlockedDate(b.date)} className="text-red-400 hover:text-red-300 text-sm font-medium shrink-0" data-testid={`remove-blocked-${b.date}`}>Remove</button>
                </div>
              ))}
            </div>
            <div className="grid sm:grid-cols-[180px_1fr_auto] gap-3 items-end mb-6">
              <div>
                <label className="block text-sm text-zinc-300 mb-2">Date to block</label>
                <input type="date" value={newBlockDate} onChange={(e) => setNewBlockDate(e.target.value)}
                  className="w-full bg-zinc-950 border border-zinc-700 rounded-md px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                  data-testid="new-blocked-date-input" />
              </div>
              <div>
                <label className="block text-sm text-zinc-300 mb-2">Message (optional)</label>
                <input type="text" value={newBlockMsg} onChange={(e) => setNewBlockMsg(e.target.value)}
                  placeholder="We're fully booked — call 321.300.3256 for the next opening."
                  className="w-full bg-zinc-950 border border-zinc-700 rounded-md px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                  data-testid="new-blocked-msg-input" />
              </div>
              <Button onClick={addBlockedDate} variant="outline" className="border-zinc-700 text-zinc-200 hover:bg-white/5" data-testid="add-blocked-date-btn">Add Date</Button>
            </div>

            {/* Date fees */}
            <h3 className="text-lg font-semibold text-white mb-3">Holiday / Date Fees</h3>
            <p className="text-sm text-zinc-400 mb-4">Automatically add a fee to any inspection scheduled on a specific date (e.g. a holiday surcharge). Shown on the customer's total.</p>
            <div className="space-y-2 mb-5" data-testid="date-fees-list">
              {settings.dateSurcharges.length === 0 && <p className="text-sm text-zinc-500">No date fees.</p>}
              {settings.dateSurcharges.map((d) => (
                <div key={d.date} className="flex items-center justify-between gap-3 bg-zinc-950 border border-zinc-800 rounded-lg px-4 py-3" data-testid={`date-fee-${d.date}`}>
                  <div className="min-w-0">
                    <p className="text-white font-medium">{d.date} — ${Number(d.amount).toFixed(2)}</p>
                    <p className="text-xs text-zinc-400 mt-0.5">{d.label}</p>
                  </div>
                  <button onClick={() => removeDateFee(d.date)} className="text-red-400 hover:text-red-300 text-sm font-medium shrink-0" data-testid={`remove-date-fee-${d.date}`}>Remove</button>
                </div>
              ))}
            </div>
            <div className="grid sm:grid-cols-[180px_120px_1fr_auto] gap-3 items-end mb-6">
              <div>
                <label className="block text-sm text-zinc-300 mb-2">Fee date</label>
                <input type="date" value={newFeeDate} onChange={(e) => setNewFeeDate(e.target.value)}
                  className="w-full bg-zinc-950 border border-zinc-700 rounded-md px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                  data-testid="new-fee-date-input" />
              </div>
              <div>
                <label className="block text-sm text-zinc-300 mb-2">Amount ($)</label>
                <input type="number" min="0" step="0.01" value={newFeeAmount} onChange={(e) => setNewFeeAmount(e.target.value)}
                  placeholder="49"
                  className="w-full bg-zinc-950 border border-zinc-700 rounded-md px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                  data-testid="new-fee-amount-input" />
              </div>
              <div>
                <label className="block text-sm text-zinc-300 mb-2">Label</label>
                <input type="text" value={newFeeLabel} onChange={(e) => setNewFeeLabel(e.target.value)}
                  className="w-full bg-zinc-950 border border-zinc-700 rounded-md px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                  data-testid="new-fee-label-input" />
              </div>
              <Button onClick={addDateFee} variant="outline" className="border-zinc-700 text-zinc-200 hover:bg-white/5" data-testid="add-date-fee-btn">Add Fee</Button>
            </div>

            <Button onClick={saveSettings} disabled={savingSettings} className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white" data-testid="save-settings-btn">
              {savingSettings ? 'Saving…' : 'Save Scheduling Settings'}
            </Button>
            <p className="text-xs text-zinc-500 mt-4">Pricing: Certified $199 · Exotic $299 · Road Trip $99. Add-ons: Carfax +$59.99 · Negotiation +$49 · Live Review & Consultation +$29 · Same-Day +$49 (auto).</p>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid md:grid-cols-4 gap-6 mb-12">
          <div className="bg-gradient-to-br from-zinc-900 to-zinc-950 rounded-2xl border border-zinc-800 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-zinc-600/10 border border-zinc-600/30 flex items-center justify-center">
                <Calendar className="w-6 h-6 text-zinc-600" />
              </div>
            </div>
            <p className="text-3xl font-bold text-white" data-testid="total-bookings-stat">{stats.totalBookings}</p>
            <p className="text-zinc-400 text-sm mt-1">Total Bookings</p>
          </div>

          <div className="bg-gradient-to-br from-zinc-900 to-zinc-950 rounded-2xl border border-zinc-800 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-yellow-500/10 border border-yellow-500/30 flex items-center justify-center">
                <Clock className="w-6 h-6 text-yellow-500" />
              </div>
            </div>
            <p className="text-3xl font-bold text-white" data-testid="pending-bookings-stat">{stats.pendingBookings}</p>
            <p className="text-zinc-400 text-sm mt-1">Pending</p>
          </div>

          <div className="bg-gradient-to-br from-zinc-900 to-zinc-950 rounded-2xl border border-zinc-800 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-green-500/10 border border-green-500/30 flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-green-500" />
              </div>
            </div>
            <p className="text-3xl font-bold text-white" data-testid="completed-inspections-stat">{stats.completedInspections}</p>
            <p className="text-zinc-400 text-sm mt-1">Completed</p>
          </div>

          <div className="bg-gradient-to-br from-zinc-900 to-zinc-950 rounded-2xl border border-zinc-800 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-orange-500/10 border border-orange-500/30 flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-orange-500" />
              </div>
            </div>
            <p className="text-3xl font-bold text-white" data-testid="total-revenue-stat">${stats.totalRevenue.toFixed(2)}</p>
            <p className="text-zinc-400 text-sm mt-1">Total Revenue</p>
          </div>
        </div>

        {/* Referral Source Chart — where customers come from */}
        <div className="bg-gradient-to-br from-zinc-900 to-zinc-950 rounded-2xl border border-zinc-800 p-6 md:p-8 mb-12" data-testid="referral-chart">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-orange-500/10 border border-orange-500/30 flex items-center justify-center">
              <BarChart3 className="w-5 h-5 text-orange-500" />
            </div>
            <h2 className="text-2xl font-semibold text-white">Where Customers Come From</h2>
          </div>
          {(() => {
            const ORDER = ['Google', 'Yelp', 'ChatGPT or AI', 'Social Media', 'Other'];
            const counts = ORDER.reduce((a, k) => ({ ...a, [k]: 0 }), {});
            let known = 0;
            bookings.forEach((b) => {
              const s = b.referralSource;
              if (s && counts[s] !== undefined) { counts[s]++; known++; }
              else if (s) { counts['Other']++; known++; }
            });
            const max = Math.max(1, ...ORDER.map((k) => counts[k]));
            if (known === 0) {
              return <p className="text-zinc-500 text-sm">No referral data yet — it will appear here as customers book and tell us how they heard about CarLens.</p>;
            }
            return (
              <div className="space-y-4">
                {ORDER.map((k) => {
                  const c = counts[k];
                  const pct = known ? Math.round((c / known) * 100) : 0;
                  return (
                    <div key={k} data-testid={`referral-row-${k.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-sm text-zinc-300">{k}</span>
                        <span className="text-sm font-semibold text-white">{c} <span className="text-zinc-500 font-normal">({pct}%)</span></span>
                      </div>
                      <div className="h-3 w-full rounded-full bg-zinc-800 overflow-hidden">
                        <div className="h-full rounded-full bg-gradient-to-r from-orange-500 to-orange-600 transition-all duration-500" style={{ width: `${(c / max) * 100}%` }} />
                      </div>
                    </div>
                  );
                })}
                <p className="text-xs text-zinc-500 pt-2">Based on {known} booking{known === 1 ? '' : 's'} with a referral source.</p>
              </div>
            );
          })()}
        </div>

        {/* Bookings List */}
        <div className="bg-gradient-to-br from-zinc-900 to-zinc-950 rounded-2xl border border-zinc-800 p-6 md:p-8">
          <h2 className="text-2xl font-semibold text-white mb-6">Recent Bookings</h2>

          <div className="space-y-4">
            {bookings.map((booking) => (
              <div key={booking.id} className="rounded-xl border border-zinc-800 bg-white/[0.02] p-5 hover:bg-white/[0.04] transition-colors" data-testid={`booking-card-${booking.id}`}>
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                  {/* Info */}
                  <div className="flex-1 grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <div>
                      <p className="text-xs text-zinc-500 uppercase tracking-wide mb-1">Customer</p>
                      <p className="text-white font-medium">{booking.customerName}</p>
                      <p className="text-zinc-500 text-xs break-all">{booking.email}</p>
                    </div>
                    <div>
                      <p className="text-xs text-zinc-500 uppercase tracking-wide mb-1">Vehicle</p>
                      <p className="text-white">{booking.carYear} {booking.carMake} {booking.carModel}</p>
                    </div>
                    <div>
                      <p className="text-xs text-zinc-500 uppercase tracking-wide mb-1">Date & Time</p>
                      <p className="text-white">{booking.preferredDate}</p>
                      <p className="text-zinc-500 text-xs">{booking.preferredTime}</p>
                    </div>
                    <div>
                      <p className="text-xs text-zinc-500 uppercase tracking-wide mb-1">Amount</p>
                      <p className="text-white font-semibold">${(booking.totalAmount || booking.amount || 0).toFixed(2)}</p>
                    </div>
                  </div>

                  {/* Status + Actions */}
                  <div className="flex flex-col sm:flex-row lg:flex-col xl:flex-row items-start sm:items-center gap-3 lg:min-w-[280px] lg:justify-end">
                    <div className="flex gap-2">
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(booking.status)}`}>
                        {statusLabels[booking.status] || booking.status}
                      </span>
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium border ${
                        booking.paymentStatus === 'paid'
                          ? 'bg-green-500/10 text-green-500 border-green-500/30'
                          : 'bg-yellow-500/10 text-yellow-500 border-yellow-500/30'
                      }`}>
                        {booking.paymentStatus}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {(booking.status === 'pending_review' || booking.status === 'pending') && (
                        <Button
                          size="sm"
                          className="bg-orange-500 hover:bg-orange-600 text-white"
                          onClick={() => openReview(booking)}
                          data-testid={`review-booking-${booking.id}`}
                        >
                          Review & Approve
                        </Button>
                      )}
                      {booking.status === 'approved_awaiting_payment' && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-zinc-700 text-zinc-300 hover:bg-white/5"
                          onClick={() => { navigator.clipboard.writeText(booking.paymentLink || ''); toast.success('Payment link copied'); }}
                          data-testid={`copy-link-${booking.id}`}
                        >
                          Copy Link
                        </Button>
                      )}
                      {(booking.status === 'pending_review' || booking.status === 'pending' || booking.status === 'approved_awaiting_payment' || booking.status === 'expired') && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-green-500/40 text-green-400 hover:bg-green-500/10"
                          onClick={() => markConfirmed(booking.id)}
                          data-testid={`mark-confirmed-${booking.id}`}
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Mark Confirmed
                        </Button>
                      )}
                      {booking.status === 'confirmed' && (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-zinc-700 text-zinc-300 hover:bg-white/5"
                            onClick={() => addToCalendar(booking)}
                            data-testid={`add-calendar-${booking.id}`}
                          >
                            <CalendarPlus className="w-4 h-4 mr-1" />
                            Calendar
                          </Button>
                          <Button
                            size="sm"
                            className="bg-zinc-600 hover:bg-zinc-700 text-white"
                            onClick={() => window.open('https://carlens-inspect.base44.app', '_blank', 'noopener,noreferrer')}
                            data-testid={`start-inspection-${booking.id}`}
                          >
                            Begin Inspection
                          </Button>
                        </>
                      )}
                      {booking.status === 'completed' && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-zinc-700 text-zinc-300 hover:bg-white/5"
                          onClick={() => navigate(`/report/${booking.id}`)}
                          data-testid={`view-report-${booking.id}`}
                        >
                          View Report
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {bookings.length === 0 && (
              <div className="text-center py-12">
                <p className="text-zinc-500">No bookings yet</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Review & Approve Dialog */}
      <Dialog open={!!reviewBooking} onOpenChange={(open) => !open && setReviewBooking(null)}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-white max-w-lg" data-testid="review-dialog">
          <DialogHeader>
            <DialogTitle className="text-white">Verify Booking Request</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Confirm the details before sending a secure payment link.
            </DialogDescription>
          </DialogHeader>

          {reviewBooking && (
            <div className="space-y-5">
              <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-4 text-sm space-y-1">
                <p className="text-white font-semibold">{reviewBooking.carYear} {reviewBooking.carMake} {reviewBooking.carModel}</p>
                <p className="text-zinc-400">{reviewBooking.customerName} · {reviewBooking.email} · {reviewBooking.phone}</p>
                <p className="text-zinc-400">Location: {reviewBooking.location}</p>
                <p className="text-zinc-400">Requested: {reviewBooking.preferredDate} at {reviewBooking.preferredTime}</p>
                <p className="text-zinc-400">Heard about us: <span className="text-zinc-200">{reviewBooking.referralSource || 'Not specified'}</span></p>
                <p className="text-orange-400 font-semibold pt-1" data-testid="review-total">Total: ${((reviewBooking.totalAmount || 0) - (reviewBooking.travelFee || 0) + Math.max(0, parseFloat(feeInput) || 0)).toFixed(2)}</p>
              </div>

              {/* Travel commitment (internal — origin is never shown) */}
              <div className={`rounded-xl border p-4 text-sm space-y-2 ${reviewBooking.travelApprovalRequired ? 'border-orange-500/50 bg-orange-500/10' : 'border-zinc-800 bg-zinc-900'}`} data-testid="admin-travel-info">
                <p className="text-white font-semibold flex items-center gap-2">Travel Information
                  <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${reviewBooking.travelApprovalRequired ? 'bg-orange-500 text-white' : 'bg-zinc-700 text-zinc-200'}`}>
                    {reviewBooking.travelZoneLabel || 'Standard Service Area'}
                  </span>
                </p>
                <p className="text-zinc-400">One-way: {reviewBooking.travelOneWayMinutes != null ? `${reviewBooking.travelOneWayMinutes} min` : 'Not calculated — verify manually'}</p>
                <p className="text-zinc-400">Round trip: {reviewBooking.travelRoundTripMinutes != null ? `${reviewBooking.travelRoundTripMinutes} min` : 'Not calculated — verify manually'}</p>
                <div className="pt-1">
                  <label className="block text-zinc-300 font-medium mb-1" htmlFor="travel-fee-input">
                    Travel fee (editable)
                    {(reviewBooking.travelZone === 'manual_review' || reviewBooking.travelZone === 'uncomputed') && (
                      <span className="ml-2 text-[11px] text-orange-300 font-semibold">Auto-calc unavailable — please set this before approving</span>
                    )}
                  </label>
                  <div className="flex items-center gap-2">
                    <span className="text-zinc-400">$</span>
                    <input
                      id="travel-fee-input"
                      type="number"
                      min="0"
                      step="0.01"
                      value={feeInput}
                      onChange={(e) => setFeeInput(e.target.value)}
                      className="w-32 bg-zinc-950 border border-zinc-700 rounded-md px-3 py-1.5 text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                      data-testid="travel-fee-input"
                    />
                    {reviewBooking.travelComputed && (
                      <span className="text-[11px] text-zinc-500">auto-calculated: ${(reviewBooking.travelFee || 0).toFixed(2)}</span>
                    )}
                  </div>
                </div>
                <p className={`font-semibold ${reviewBooking.travelApprovalRequired ? 'text-orange-300' : 'text-green-400'}`}>Approval required: {reviewBooking.travelApprovalRequired ? 'YES' : 'NO'}</p>
              </div>

              <div className="space-y-3">
                <label className="flex items-start gap-3 cursor-pointer" data-testid="check-seller">
                  <Checkbox checked={checks.seller} onCheckedChange={(v) => setChecks({ ...checks, seller: !!v })} className="mt-0.5 border-zinc-600" />
                  <span className="text-zinc-300 text-sm">The seller has agreed to the inspection.</span>
                </label>
                <label className="flex items-start gap-3 cursor-pointer" data-testid="check-location">
                  <Checkbox checked={checks.location} onCheckedChange={(v) => setChecks({ ...checks, location: !!v })} className="mt-0.5 border-zinc-600" />
                  <span className="text-zinc-300 text-sm">The location is within our service area.</span>
                </label>
                <label className="flex items-start gap-3 cursor-pointer" data-testid="check-time">
                  <Checkbox checked={checks.time} onCheckedChange={(v) => setChecks({ ...checks, time: !!v })} className="mt-0.5 border-zinc-600" />
                  <span className="text-zinc-300 text-sm">The requested time is realistic.</span>
                </label>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="outline"
              className="border-red-500/40 text-red-400 hover:bg-red-500/10"
              onClick={() => declineBooking(reviewBooking.id)}
              data-testid="decline-booking-btn"
            >
              Decline
            </Button>
            <Button
              className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white"
              disabled={!allChecked || approving}
              onClick={approveBooking}
              data-testid="approve-send-link-btn"
            >
              {approving ? 'Sending...' : 'Approve & Send Payment Link'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;